import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SectionContainer, SectionTitle } from '../common/styles';
import Actions from '../common/actions';
import Filters from '../common/filters';
import { DATE_RANGES } from '../common/constants';
import Loader from '../common/loader';
import { getContributorRanking } from '../../../services/analytics/audience-service';
import useLeaderboardStore from './contributor-leaderboard-components/state-hook';
import Description from './contributor-leaderboard-components/description';
import Ranking from './contributor-leaderboard-components/ranking';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 56px;

  & > *:first-child {
    order: -1;
    /* The whole desktop view only supports two resolutions: XLarge(1024px~1365px) and XXLarge(1366px+) */
    flex-basis: ${props => (props.theme.isXXLarge ? '33%' : '24%')};
  }

  &:after {
    order: 0;
    height: 100%;
    content: "";
    width: 22px;
    display: block;
  }

  & > *:last-child {
    order: 1;
    /* The whole desktop view only supports two resolutions: XLarge(1024px~1365px) and XXLarge(1366px+) */
    flex-basis: ${props => (props.theme.isXXLarge ? '67%' : '76%')};
    margin-top: 12px;
  }
`;

export default function ContributorLeaderboard({
  id, contributors, contributorCount, cachedAnalyticsDataUrls,
}) {
  const cachedContributorRankingDataUrl = cachedAnalyticsDataUrls.contributorRankingDataUrl;
  const setContributors = useLeaderboardStore(state => state.setContributors);
  const setDaysAgo = useLeaderboardStore(state => state.setDaysAgo);
  const set = useLeaderboardStore(state => state.set);
  const setRankingStore = useLeaderboardStore(state => state.setRankingStore);
  const storeContribs = useLeaderboardStore(state => state.contributors);
  const daysAgo = useLeaderboardStore(state => state.daysAgo);
  const generatedAt = useLeaderboardStore(state => state.generatedAt);
  const requestData = useLeaderboardStore(state => state.requestData);
  const storeLoading = useLeaderboardStore(state => state.loading);
  const [loading, setLoading] = React.useState(false);
  const [inited, setInited] = React.useState(false);
  const [token, setToken] = React.useState(null);

  const fetch = React.useCallback(({ full = false, fromCache = false } = {}) => {
    if (!_.get(contributors, 'length')) {
      return;
    }

    setLoading(true);
    const jobs = DATE_RANGES
      .filter(d => (full || d.value === daysAgo))
      .map(d => {
        const [task, source] = getContributorRanking(
          id,
          Number.isFinite(d.value) ? d.value : 0,
          cachedContributorRankingDataUrl,
          fromCache,
        );
        return { task, source };
      });
    !full && setToken(jobs[0].source);
    Promise.all(jobs.map(j => j.task))
      .then(responses => {
        responses
          .map((res, i) => ({
            data: _.get(res, 'data', []),
            date: full ? DATE_RANGES[i].value : daysAgo,
          }))
          .forEach(d => {
            if (d.data.length) {
              setInited(true);
              set(state => {
                state.generatedAt = moment();
              });
              setRankingStore(d);
            }
          });
      })
      .finally(() => {
        set(state => {
          state.requestData = false;
        });
        setLoading(false);
        !full && setToken(null);
      });
  }, [contributors, id, daysAgo, set, setRankingStore, cachedContributorRankingDataUrl]);

  React.useEffect(() => {
    if (!inited) {
      setContributors(_.map(contributors, c => ({ ...c, selected: true })));
      fetch({ full: true, fromCache: true });
    }
  }, [contributors, inited, fetch, setContributors]);

  React.useEffect(() => {
    requestData && fetch({ fromCache: false });
  }, [requestData, fetch]);

  React.useEffect(() => {
    let tid;
    if (loading) {
      tid = setTimeout(() => set(state => {
        state.loading = true;
      }), 200);
    } else {
      set(state => {
        state.loading = false;
      });
    }
    return () => clearTimeout(tid);
  }, [loading, set]);

  const onCancel = () => token && token.cancel();

  const filtersProps = {
    daysAgo,
    contributors: storeContribs,
    onContributorsChange: setContributors,
    onDateChange: setDaysAgo,
  };

  const actionsProps = {
    generatedAt,
    onRefreshClick: () => set(state => {
      state.requestData = true;
    }),
  };

  return (
    <SectionContainer>
      {!!storeLoading && <Loader cancellable={inited} onCancel={onCancel} />}
      <Filters {...filtersProps} />
      <Actions {...actionsProps} />
      <SectionTitle>contributor leaderboard</SectionTitle>
      <Wrapper>
        <Description />
        <Ranking contributorCount={contributorCount} />
      </Wrapper>
    </SectionContainer>
  );
}

ContributorLeaderboard.propTypes = {
  id: PropTypes.number.isRequired,
  cachedAnalyticsDataUrls: PropTypes.shape({
    contributorRankingDataUrl: PropTypes.string,
  }).isRequired,
  contributors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    profilePictureUrl: PropTypes.string,
    createdAt: PropTypes.string,
  })).isRequired,
  contributorCount: PropTypes.number.isRequired,
};
